import type { CreateAdminWorkflowDto, UpdateAdminWorkflowDto } from '@idc-forms/api-client'
import type {
  V2CreateAdminWorkflowInput,
  V2UpdateAdminWorkflowInput
} from '@idc-forms/schemas/api-v2/admin-workflows'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import useAuth from '@/features/auth/hooks/use-auth'
import { queryKeys } from '@/shared/constants/query-keys'
import { getErrorMessage } from '@/shared/utils/handle-api-error'
import { workflowsAdminV2Service } from '../services/workflows-admin-v2.service'

/**
 * Mutaciones de flujos de trabajo contra la API v2. Identifican por id público
 * (`wfl_`) y, al completar, invalidan todo el árbol `['workflows']` (listado +
 * summary + lookup + detalle). El alta/edición vive en el side panel.
 */
function useV2AccessToken() {
  const { auth } = useAuth()
  return auth?.v2AccessToken ?? ''
}

export const useCreateWorkflowV2Mutation = () => {
  const queryClient = useQueryClient()
  const accessToken = useV2AccessToken()
  return useMutation({
    // Se acepta el tipo de DOMINIO (schema Zod) y se castea en el borde del SDK:
    // el generado y el schema describen el mismo dato en runtime pero TS los ve
    // como tipos distintos.
    mutationFn: async (body: V2CreateAdminWorkflowInput) =>
      await workflowsAdminV2Service.create(accessToken, body as CreateAdminWorkflowDto),
    onSuccess: () => {
      toast.success('Flujo creado')
      void queryClient.invalidateQueries({ queryKey: queryKeys.workflows.all })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    }
  })
}

export const useUpdateWorkflowV2Mutation = () => {
  const queryClient = useQueryClient()
  const accessToken = useV2AccessToken()
  return useMutation({
    mutationFn: async ({ id, body }: { id: string; body: V2UpdateAdminWorkflowInput }) =>
      await workflowsAdminV2Service.update(accessToken, id, body as UpdateAdminWorkflowDto),
    onSuccess: () => {
      toast.success('Flujo actualizado')
      void queryClient.invalidateQueries({ queryKey: queryKeys.workflows.all })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    }
  })
}

export const useDeleteWorkflowV2Mutation = () => {
  const queryClient = useQueryClient()
  const accessToken = useV2AccessToken()
  return useMutation({
    mutationFn: async (id: string) => await workflowsAdminV2Service.remove(accessToken, id),
    onSuccess: () => {
      toast.success('Flujo eliminado')
      void queryClient.invalidateQueries({ queryKey: queryKeys.workflows.all })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    }
  })
}
